import { computed, unref } from "vue";
import type { Ref } from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import CustomField from "@/services/custom-field/custom-field";
import type {
  CreateCustomFieldDto,
  CustomFieldDefinition,
  CustomFieldObjectType,
  UpdateCustomFieldDto,
} from "@/dtos/CustomField.dto";

export function useCustomFields(
  objectType: Ref<CustomFieldObjectType | undefined> | CustomFieldObjectType | undefined = undefined
) {
  const queryKey = computed(() => {
    const t = unref(objectType);
    return t ? ["customFields", t] : ["customFields"];
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const t = unref(objectType);
      const result = await CustomField.getCustomFields(t ? { objectType: t } : {});
      if (result.data.status) return result.data.data;
      throw new Error(result.data.msg || "Failed to load custom fields");
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Same as useCustomFields, but pre-filtered to active, non-deleted fields and
 * sorted by position. Use this everywhere fields are rendered/displayed
 * (forms, filters, tables, overview pages) so a deactivated or soft-deleted
 * field can't be edited/filtered on after the fact.
 */
export function useActiveCustomFields(
  objectType: Ref<CustomFieldObjectType | undefined> | CustomFieldObjectType | undefined = undefined
) {
  const query = useCustomFields(objectType);
  const data = computed(() =>
    ((query.data.value as CustomFieldDefinition[]) || [])
      .filter((f) => f.isActive && !f.isDeleted)
      .sort((a, b) => a.position - b.position)
  );
  return { ...query, data };
}

export function useCreateCustomField() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateCustomFieldDto) => {
      const result = await CustomField.createCustomField(data);
      if (!result.data.status) throw new Error(result.data.msg || "Failed to create field");
      return result.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customFields"] });
    },
  });
}

export function useUpdateCustomField() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateCustomFieldDto }) => {
      const result = await CustomField.updateCustomField(id, data);
      if (!result.data.status) throw new Error(result.data.msg || "Failed to update field");
      return result.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customFields"] });
    },
  });
}

export function useDeleteCustomField() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await CustomField.deleteCustomField(id);
      if (!result.data.status) throw new Error(result.data.msg || "Failed to delete field");
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customFields"] });
    },
  });
}
